import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createBlock as _createBlock, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "label required-container"
}
const _hoisted_2 = {
  key: 0,
  class: "required-label"
}
const _hoisted_3 = {
  class: "property-lookup-container",
  "data-bind": "dxTextBox: {}"
}

import { ref, computed, inject, onMounted, watch } from "vue";
import FindPropertyModal from "@/components/find/properties/find-property-modal.vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import DxTextBox from "devextreme-vue/text-box";
import { DxButton } from "devextreme-vue/text-box";
import { PropertyFindResult } from "@/types/find/properties/PropertyFindResult";
import { DxRequiredRule } from "devextreme-vue/validator";
import { DxValidator } from "devextreme-vue";
import { useRouter } from "vue-router";
import { RouteNames } from "@/router/RouteNames";
import PropertyPeekModal from "@/components/properties/property-peek-modal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'property-lookup',
  props: {
  /**
   * The numeric value used with `v-model` to bind the component state. This typically represents an ID or a selected value.
   * @type {number}
   * @required
   */
  modelValue: {
    type: Number,
    required: true,
  },
  /**
   * Determines whether the control is read only.
   * @type {boolean}
   * @default false
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /**
   * The label displayed for the control.
   * @type {String}
   * @default ''
   */
  label: {
    type: String,
    default: "",
  },
  /**
   * Determines whether the control is required.
   * @type {boolean}
   * @default false
   */
  required: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to allow to reset validation.
   * @type {boolean}
   * @default false
   */
  resetValid: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to display Navigate Icon.
   * @type {boolean}
   * @default true
   */
  displayNavigateIcon: {
    type: Boolean,
    default: true,
  },
  /**
   * Determines whether to display Peek.
   * @type {boolean}
   * @default false
   */
  displayPeek: {
    type: Boolean,
    default: false,
  },
  /**
   * The validation group identifier for managing form validations.
   * @type {string}
   * @default ''
   */
  validationGroup: {
    type: String,
    required: false,
    default: "",
  },
  /**
   * Determines whether to show Clear Icon.
   * @type {boolean}
   * @default false
   */
  showClearIcon: {
    type: Boolean,
    default: false,
  },
  /**
   * Specifies the default property category to search for.
   * @type {Number}
   * @default -1 (no specific category)
   */
  defaultCategory: {
    type: Number,
    default: -1,
  },
  /**
   * Determines whether the contact is company account.
   * @type {boolean}
   * @default false
   */
  isCompanyAccount: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether include property id.
   * @type {boolean}
   * @default false
   */
  includePropertyId: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether clicking the navigation button will go to the page in the router.
   * False will only emit that the navigate button has been clicked
   * @type {boolean}
   * @default true
   */
  navigateButtonOpensPage: {
    type: Boolean,
    default: true,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Triggered when an address is selected from the search results.
   * @param {PropertyFindResult} address - The details of the selected property.
   */
  "addressSelected",
  /**
   * Triggered when the user clicks to navigate to the selected contact's details page.
   */
  "navigateToProperty",
],
  setup(__props, { emit }) {

const props = __props;





const propertyId = computed({
  get: () => props.modelValue,
  set: (value: number) => emit("update:modelValue", value),
});

const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const showModal = ref(false);
const propertyAddress = ref("");
const router = useRouter();
const displayPropertyPeekModal = ref(false);
const propertyPeekTitle = computed(() => {
  return propertyAddress.value.includes(`(${propertyId.value})`)
    ? propertyAddress.value
    : `${propertyAddress.value} (${propertyId.value})`;
});

onMounted(async () => {
  await getPropertyAddress();
});

watch(
  () => props.modelValue,
  async () => {
    await getPropertyAddress();
  },
);

const getPropertyAddress = async () => {
  // No need to call api if no property has been selected
  if (propertyId.value <= 0) {
    propertyAddress.value = "";
    return;
  }

  // When the component loads, populate the property's address so user can see which property is currently selected
  const property: any = await apiClient.httpGet(
    `database/properties/${propertyId.value}`,
  );
  propertyAddress.value = [
    property.propertyName,
    property.street,
    property.locality,
    property.town,
    property.region,
    property.postcode,
  ]
    .filter((word: string) => !!word)
    .join(", ");

  if (props.includePropertyId) {
    propertyAddress.value += ` (${property.id})`;
  }
};

/** Handles the property selected event of the find property modal */
const onPropertySelected = (e: PropertyFindResult) => {
  if (!e) {
    return;
  }

  propertyAddress.value = e.address;
  propertyId.value = e.id;
  emit("addressSelected", e.address);
};

const searchClick = () => {
  if (props.readOnly) {
    return;
  }
  showModal.value = true;
};

const navigateToProperty = () => {
  emit("navigateToProperty");
  if (!props.navigateButtonOpensPage) return;
  setTimeout(
    () =>
      router.push({
        name: RouteNames.PropertyPage,
        params: { propertyid: propertyId.value },
      }),
    500,
  );
};

const clearClick = () => {
  propertyAddress.value = "";
  propertyId.value = 0;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("span", null, _toDisplayString(__props.label), 1),
          (props.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, "Required"))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(DxTextBox), {
        value: propertyAddress.value,
        "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((propertyAddress).value = $event)),
        "read-only": true
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(DxButton), {
            name: "clear-text",
            position: "after",
            options: {
          icon: 'clear',
          stylingMode: 'text',
          type: 'dx-show-clear-button',
          visible: props.showClearIcon && propertyId.value > 0 && !props.readOnly,
          disabled: false,
          onClick: () => clearClick(),
        }
          }, null, 8, ["options"]),
          _createVNode(_unref(DxButton), {
            name: "navigate-to-property",
            position: "after",
            options: {
          icon: 'fas fa-house',
          stylingMode: 'text',
          type: 'dx-dropdowneditor-button',
          disabled: false,
          visible: propertyId.value > 0 && props.displayNavigateIcon,
          onClick: () => navigateToProperty(),
        }
          }, null, 8, ["options"]),
          _createVNode(_unref(DxButton), {
            name: "peek-contact",
            position: "after",
            options: {
          icon: 'fas fa-eye',
          stylingMode: 'text',
          type: 'dx-dropdowneditor-button',
          disabled: false,
          visible: propertyId.value > 0 && props.displayPeek,
          onClick: () => (displayPropertyPeekModal.value = true),
        }
          }, null, 8, ["options"]),
          _createVNode(_unref(DxButton), {
            name: "property-lookup",
            position: "after",
            options: {
          icon: 'fas fa-search',
          stylingMode: 'text',
          type: 'dx-dropdowneditor-button',
          visible: !props.readOnly,
          disabled: false,
          onClick: () => searchClick(),
        }
          }, null, 8, ["options"]),
          _createVNode(_unref(DxValidator), {
            "validation-group": props.validationGroup
          }, {
            default: _withCtx(() => [
              (props.required)
                ? (_openBlock(), _createBlock(_unref(DxRequiredRule), {
                    key: 0,
                    message: ""
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["validation-group"])
        ]),
        _: 1
      }, 8, ["value"])
    ]),
    _createVNode(FindPropertyModal, {
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showModal).value = $event)),
      onPropertySelected: onPropertySelected,
      defaultCategory: __props.defaultCategory
    }, null, 8, ["modelValue", "defaultCategory"]),
    _createVNode(PropertyPeekModal, {
      modelValue: displayPropertyPeekModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((displayPropertyPeekModal).value = $event)),
      "property-id": propertyId.value,
      title: propertyPeekTitle.value
    }, null, 8, ["modelValue", "property-id", "title"])
  ], 64))
}
}

})